import { useTranslate } from 'react-admin';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useResponsive } from 'src/hooks/use-responsive';

import { RECONCILIATION_NS as NS, TEXT_MAX } from './constants';
import { namesBreak } from './helpers';
import type { ReconciliationActionsForm } from './useReconciliationActions';

/**
 * XBIT-4864: the confirm of Propose ADJ, Approve & resolve and Cancel. Plain, like the closure confirm (the Figma confirm
 * is owed, Module PRD §10). A refusal stays in the dialog as the engine words it; the retry button resends.
 */
export const ActionDialog = ({
  form,
  vaultName,
}: {
  form: ReconciliationActionsForm;
  vaultName: string;
}) => {
  const translate = useTranslate();
  const smDown = useResponsive('down', 'sm');
  const t = (key: string, options?: object) => translate(`${NS}.dialog.${key}`, options);
  const { mode, target, proposal, errors } = form;
  const fieldError = (name: string) =>
    errors[name] ? translate(errors[name] as string, { max: TEXT_MAX }) : undefined;
  const resolves = mode === 'approve' && target !== null && namesBreak(target);

  return (
    <Dialog
      open={mode !== null}
      onClose={form.submitting ? undefined : form.close}
      maxWidth="sm"
      fullWidth
      fullScreen={smDown}
    >
      {mode && (
        <>
          <DialogTitle>{t(`${mode}Title`, { name: vaultName })}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2">{t(`${mode}Body`)}</Typography>
              {form.alert && (
                <Alert severity={form.alert.severity}>
                  <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                    {form.alert.messages.map((message) => (
                      <li key={message}>{message}</li>
                    ))}
                  </Stack>
                </Alert>
              )}
              {mode === 'propose' && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={proposal.shareNeutral}
                        onChange={(e) => form.patchProposal({ shareNeutral: e.target.checked })}
                      />
                    }
                    label={t('shareNeutral')}
                  />
                  {!proposal.shareNeutral && (
                    <>
                      <TextField
                        required
                        fullWidth
                        label={t('holderId')}
                        value={proposal.holderId}
                        error={Boolean(errors.holderId)}
                        helperText={fieldError('holderId') ?? t('holderIdHint')}
                        onChange={(e) => form.patchProposal({ holderId: e.target.value })}
                        inputProps={{ spellCheck: false }}
                      />
                      <TextField
                        required
                        fullWidth
                        label={t('deltaShares')}
                        value={proposal.deltaShares}
                        error={Boolean(errors.deltaShares)}
                        helperText={fieldError('deltaShares') ?? t('deltaSharesHint')}
                        onChange={(e) => form.patchProposal({ deltaShares: e.target.value })}
                        inputProps={{ inputMode: 'decimal', spellCheck: false }}
                      />
                    </>
                  )}
                  <TextField
                    required
                    fullWidth
                    label={t('reasonCode')}
                    value={proposal.reasonCode}
                    error={Boolean(errors.reasonCode)}
                    helperText={fieldError('reasonCode')}
                    onChange={(e) => form.patchProposal({ reasonCode: e.target.value })}
                  />
                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    label={t('memo')}
                    value={proposal.memo}
                    error={Boolean(errors.memo)}
                    helperText={fieldError('memo')}
                    onChange={(e) => form.patchProposal({ memo: e.target.value })}
                  />
                  <TextField
                    required
                    fullWidth
                    label={t('evidenceRef')}
                    value={proposal.evidenceRef}
                    error={Boolean(errors.evidenceRef)}
                    helperText={fieldError('evidenceRef') ?? t('evidenceRefHint')}
                    onChange={(e) => form.patchProposal({ evidenceRef: e.target.value })}
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label={t('breakValuedAt')}
                      value={proposal.breakValuedAt}
                      error={Boolean(errors.breakValuedAt)}
                      helperText={fieldError('breakValuedAt') ?? t('breakHint')}
                      onChange={(e) => form.patchProposal({ breakValuedAt: e.target.value })}
                    />
                    <TextField
                      label={t('breakAttemptSeq')}
                      value={proposal.breakAttemptSeq}
                      error={Boolean(errors.breakAttemptSeq)}
                      helperText={fieldError('breakAttemptSeq')}
                      onChange={(e) => form.patchProposal({ breakAttemptSeq: e.target.value })}
                      inputProps={{ inputMode: 'numeric' }}
                      sx={{ minWidth: 140 }}
                    />
                  </Stack>
                </>
              )}
              {resolves && (
                <>
                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={2}
                    label={t('cause')}
                    value={form.cause}
                    error={Boolean(errors.cause)}
                    helperText={fieldError('cause') ?? t('causeHint')}
                    onChange={(e) => form.setCause(e.target.value)}
                  />
                  <TextField
                    required
                    fullWidth
                    label={t('evidenceRef')}
                    value={form.resolveEvidence}
                    error={Boolean(errors.evidenceRef)}
                    helperText={fieldError('evidenceRef') ?? t('evidenceRefHint')}
                    onChange={(e) => form.setResolveEvidence(e.target.value)}
                  />
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={form.close} disabled={form.submitting}>
              {t('back')}
            </Button>
            <LoadingButton
              variant="contained"
              color={mode === 'cancel' ? 'primary' : 'error'}
              onClick={form.submit}
              disabled={form.submitting}
              loading={form.submitting}
            >
              {form.alert ? t('retry') : t(`${mode}Confirm`)}
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
